import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmployeeSummary } from '../types/models';
import { OvertimeClient } from './overtimeApi';

interface ScheduleTime { start: string | null; end: string | null; }
interface ScheduleSettings {
  employees: (ScheduleTime & { employeeId: string })[];
  shifts: (ScheduleTime & { shiftType: string })[];
  overrides: (ScheduleTime & { employeeId: string; workDate: string })[];
}
interface Props { supabaseClient: OvertimeClient | null; employees: EmployeeSummary[]; }

const SHIFT_LABELS: Record<string, string> = {
  day: '日勤', early: '早番', late: '遅番', late_special: '特遅', night: '夜勤', am: '午前', pm: '午後', on_call: '宿直'
};
const time = (value: string | null | undefined) => value?.slice(0, 5) ?? '';
const emptySettings: ScheduleSettings = { employees: [], shifts: [], overrides: [] };

export const OvertimeScheduleSettings = ({ supabaseClient, employees }: Props) => {
  const [settings, setSettings] = useState<ScheduleSettings>(emptySettings);
  const [mode, setMode] = useState<'employee' | 'shift'>('employee');
  const [employeeId, setEmployeeId] = useState('');
  const [shiftType, setShiftType] = useState('day');
  const [workDate, setWorkDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const fetchSettings = useCallback(async (): Promise<ScheduleSettings> => {
    if (!supabaseClient) throw new Error('データベースに接続できません。');
    const { data, error: readError } = await supabaseClient.rpc('get_attendance_schedule_settings');
    if (readError) throw readError;
    if (!data || !Array.isArray(data.employees) || !Array.isArray(data.shifts) || !Array.isArray(data.overrides)) {
      throw new Error('予定時刻の設定を確認できませんでした。');
    }
    return {
      employees: data.employees.map((row: any) => ({ ...row, employeeId: String(row.employeeId) })),
      shifts: data.shifts,
      overrides: data.overrides.map((row: any) => ({ ...row, employeeId: String(row.employeeId) }))
    };
  }, [supabaseClient]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchSettings().then(value => { if (active) { setSettings(value); setError(null); } })
      .catch(() => { if (active) setError('勤務予定の設定を取得できませんでした。再読み込みしてください。'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fetchSettings]);

  useEffect(() => {
    const row = mode === 'shift'
      ? settings.shifts.find(value => value.shiftType === shiftType)
      : workDate ? settings.overrides.find(value => value.employeeId === employeeId && value.workDate === workDate)
        : settings.employees.find(value => value.employeeId === employeeId);
    setStart(time(row?.start));
    setEnd(time(row?.end));
  }, [mode, employeeId, workDate, shiftType, settings]);

  useEffect(() => { setNotice(null); }, [mode, employeeId, workDate, shiftType]);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try { setSettings(await fetchSettings()); }
    catch { setError('勤務予定の設定を取得できませんでした。通信状態を確認してください。'); }
    finally { setLoading(false); }
  };

  const save = async (clear: boolean) => {
    if (!supabaseClient || savingRef.current) return;
    if (mode === 'employee' && !employeeId) { setError('従業員を選択してください。'); return; }
    if (!clear && (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end))) {
      setError('予定開始時刻と予定終了時刻を入力してください。'); return;
    }
    if (!clear && start === end) { setError('開始と終了が同じ時刻の予定は登録できません。'); return; }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const { error: saveError } = await supabaseClient.rpc('save_attendance_schedule_setting', {
        p_employee_id: mode === 'employee' ? employeeId : null,
        p_shift_type: mode === 'shift' ? shiftType : null,
        p_work_date: mode === 'employee' ? workDate || null : null,
        p_start: clear ? null : start, p_end: clear ? null : end
      });
      if (saveError) throw saveError;
      const refreshed = await fetchSettings();
      const row = mode === 'shift'
        ? refreshed.shifts.find(value => value.shiftType === shiftType)
        : workDate ? refreshed.overrides.find(value => value.employeeId === employeeId && value.workDate === workDate)
          : refreshed.employees.find(value => value.employeeId === employeeId);
      // Clearing may reveal a legacy fixed-time setting; display the actual read-back rather than claiming it is empty.
      if (!clear && (time(row?.start) !== start || time(row?.end) !== end)) throw new Error('保存された予定時刻が入力内容と一致しません。再読み込みしてください。');
      setSettings(refreshed);
      setNotice(clear ? '個別の設定を解除しました。既存のシフト条件に固定時刻があれば、その時刻が再び適用されます。現在有効な予定時刻を確認してください。' : '予定時刻を保存しました。');
    } catch (err) {
      setError(err && typeof err === 'object' && 'message' in err ? String((err as any).message) : '予定時刻を保存できませんでした。');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const filteredEmployees = useMemo(() => employees.filter(employee => `${employee.name} ${employee.employeeCode ?? ''}`.toLowerCase().includes(search.toLowerCase())), [employees, search]);
  const disabled = loading || saving;
  return <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
    <div><h3 className="font-bold text-gray-900">時間外勤務の判定に使う予定時刻</h3>
      <p className="mt-1 text-sm text-gray-600">勤務日別の個人設定 → 当日に割り当てたシフトの予定時刻の順に使います。当日のシフトがない場合は個人の固定時刻、固定時刻もなければ打刻区分の共通時刻を使います。割り当てたシフトの時刻が未設定なら、確認が必要と表示します。</p>
      <p className="mt-1 text-sm text-gray-600">公休・有給の日に勤務する場合は、勤務日を指定して予定時刻を登録してください。</p></div>
    {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice && <p role="status" className="rounded bg-green-50 p-3 text-sm text-green-800">{notice}</p>}
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-sm">設定対象<select aria-label="予定時刻の設定対象" value={mode} onChange={event => setMode(event.target.value as 'employee' | 'shift')} disabled={disabled} className="mt-1 block rounded border p-2"><option value="employee">従業員ごと</option><option value="shift">シフト区分の共通時刻</option></select></label>
      {mode === 'employee' ? <>
        <label className="text-sm">従業員<select aria-label="予定時刻を設定する従業員" value={employeeId} onChange={event => setEmployeeId(event.target.value)} disabled={disabled} className="mt-1 block rounded border p-2"><option value="">選択してください</option>{employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}（{employee.employeeCode}）</option>)}</select></label>
        <label className="text-sm">勤務日（空欄で固定時刻）<input aria-label="予定時刻を設定する勤務日" type="date" value={workDate} onChange={event => setWorkDate(event.target.value)} disabled={disabled} className="mt-1 block rounded border p-2" /></label>
      </> : <label className="text-sm">シフト区分<select aria-label="予定時刻のシフト区分" value={shiftType} onChange={event => setShiftType(event.target.value)} disabled={disabled} className="mt-1 block rounded border p-2">{Object.entries(SHIFT_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>}
      <label className="text-sm">予定開始<input type="time" aria-label="予定開始時刻" value={start} onChange={event => setStart(event.target.value)} disabled={disabled} className="mt-1 block rounded border p-2" /></label>
      <label className="text-sm">予定終了<input type="time" aria-label="予定終了時刻" value={end} onChange={event => setEnd(event.target.value)} disabled={disabled} className="mt-1 block rounded border p-2" /></label>
    </div>
    {start && end && end < start && <p className="text-sm text-purple-800">終了は翌日の {end} として扱います。</p>}
    <div className="flex flex-wrap gap-2"><button type="button" onClick={() => save(false)} disabled={disabled} className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{saving ? '保存・確認中…' : '予定時刻を保存'}</button><button type="button" onClick={() => save(true)} disabled={disabled} className="rounded border px-4 py-2 disabled:opacity-50">この個別設定を解除</button><button type="button" onClick={reload} disabled={disabled} className="rounded border px-4 py-2 disabled:opacity-50">再読み込み</button></div>
    {loading ? <p role="status">予定時刻を読み込んでいます…</p> : <>
      <div className="grid gap-3 md:grid-cols-2"><div><h4 className="mb-2 text-sm font-bold">個人の固定時刻</h4><input aria-label="予定時刻の従業員検索" placeholder="氏名・従業員コードで検索" value={search} onChange={event => setSearch(event.target.value)} className="mb-2 w-full rounded border p-2 text-sm" /><div className="max-h-64 overflow-auto"><table className="w-full text-sm"><thead><tr><th className="p-2 text-left">従業員</th><th className="p-2 text-left">固定時刻</th></tr></thead><tbody>{filteredEmployees.map(employee => { const row = settings.employees.find(value => value.employeeId === employee.id); return <tr key={employee.id} className="border-t"><td className="p-2">{employee.name}</td><td className="p-2">{row?.start && row?.end ? `${time(row.start)} ～ ${time(row.end)}${time(row.end) < time(row.start) ? '（翌日）' : ''}` : '固定なし・シフトに従う'}</td></tr>; })}</tbody></table></div></div>
        <div><h4 className="mb-2 text-sm font-bold">シフト区分の共通時刻</h4><table className="w-full text-sm"><thead><tr><th className="p-2 text-left">区分</th><th className="p-2 text-left">予定時刻</th></tr></thead><tbody>{Object.entries(SHIFT_LABELS).map(([key, label]) => { const row = settings.shifts.find(value => value.shiftType === key); return <tr key={key} className="border-t"><td className="p-2">{label}</td><td className={`p-2 ${row?.start && row?.end ? '' : 'text-amber-800'}`}>{row?.start && row?.end ? `${time(row.start)} ～ ${time(row.end)}${time(row.end) < time(row.start) ? '（翌日）' : ''}` : '未設定'}</td></tr>; })}</tbody></table></div></div>
      {settings.overrides.length > 0 && <details><summary className="cursor-pointer text-sm text-blue-700">勤務日別の個人設定（{settings.overrides.length}件）</summary><div className="mt-2 max-h-64 overflow-auto text-sm">{settings.overrides.map(row => <p key={`${row.employeeId}-${row.workDate}`} className="border-b py-2">{employees.find(employee => employee.id === row.employeeId)?.name ?? row.employeeId} ／ {row.workDate} ／ {time(row.start)} ～ {time(row.end)}{time(row.end) < time(row.start) ? '（翌日）' : ''}</p>)}</div></details>}
    </>}
  </section>;
};

export default OvertimeScheduleSettings;
